import type { FindingKind } from "@shared/ocenaTypy.js";
import type { WariantWskazaniaDto } from "../../lib/api.js";
import { odmien } from "../../lib/polski.js";
import type { GrupaParametru, WskazanieWidoku } from "../../lib/raportOceny.js";
import { Badge } from "../ui/Badge.js";

/**
 * Wskazania z oceny: grupa parametru (tytul po polsku) i pojedyncze wskazanie.
 *
 * Mocna strona pokazuje: cytat, dlaczego to dobre, jak rozwijac. Rzecz do poprawy: cytat, jak to obniza wplyw
 * i 1-3 lepsze warianty z uzasadnieniem. Numer wypowiedzi (od 1, ten sam co w transkrypcie) jest przyciskiem,
 * ktory przewija transkrypt do tej wypowiedzi: to jedyny numer w interfejsie, wiec nie ma czego mylic.
 *
 * Kolor niesie ocene, nie status (decyzja autora metodyki z 29.10.2026): zloty przy mocnych stronach i lepszych
 * wariantach, szary przy rzeczach do poprawy, z tokenow --ocena-* (styles/ocena.css). Nigdy sam: zawsze razem z tekstem
 * etykiety albo plakietki (ST-3).
 */

/** Kolor etykiety i plakietki: "zloto" przy tym, co dobre (i przy lepszym wariancie), "szarosc" przy rzeczy do poprawy. */
type TonEtykiety = "zloto" | "szarosc";

/** Ton rodzaju wskazania: zloty przy mocnej stronie, szary przy rzeczy do poprawy (etykiety i plakietki). */
export function tonRodzaju(rodzaj: FindingKind): TonEtykiety {
  return rodzaj === "mocna-strona" ? "zloto" : "szarosc";
}

export function Etykieta({ ton, children }: { ton?: TonEtykiety; children: React.ReactNode }): React.JSX.Element {
  return <span className={`wskazanie__etykieta${ton ? ` wskazanie__etykieta--${ton}` : ""}`}>{children}</span>;
}

/** Lista lepszych wariantow z uzasadnieniem. Wspolna dla karty oceny i uwag przy dymku w transkrypcie. */
export function ListaWariantow({ warianty }: { warianty: readonly WariantWskazaniaDto[] }): React.JSX.Element {
  return (
    <ol className="warianty" role="list">
      {warianty.map((w, i) => (
        <li className="wariant" key={i}>
          <p className="wariant__tekst">{w.tekst}</p>
          {w.dlaczegoLepiej.trim().length > 0 && (
            <p className="wariant__powod">
              <Etykieta>Dlaczego lepiej: </Etykieta>
              {w.dlaczegoLepiej}
            </p>
          )}
        </li>
      ))}
    </ol>
  );
}

function Warianty({ wskazanie }: { wskazanie: WskazanieWidoku }): React.JSX.Element {
  const { warianty } = wskazanie;
  if (warianty.length === 0) {
    return <p className="wskazanie__uwaga">Model nie podał lepszego wariantu tej wypowiedzi.</p>;
  }

  return (
    <div className="wskazanie__warianty">
      {/* Lepszy wariant to rozwiazanie problemu, wiec ma ton mocnej strony (zloto), jak kreska .wariant w session.css. */}
      <Etykieta ton="zloto">{odmien(warianty.length, "Lepszy wariant", "Lepsze warianty", "Lepsze warianty")}</Etykieta>
      <ListaWariantow warianty={warianty} />
    </div>
  );
}

export function WskazanieOceny({
  wskazanie,
  onPokaz,
}: {
  wskazanie: WskazanieWidoku;
  /** Przewija transkrypt do wypowiedzi o tym indeksie (od 0). */
  onPokaz: (indeks: number) => void;
}): React.JSX.Element {
  const mocna = wskazanie.rodzaj === "mocna-strona";
  const { indeks, numer } = wskazanie;

  return (
    <article className="wskazanie" data-rodzaj={wskazanie.rodzaj}>
      <div className="wskazanie__head">
        {indeks !== null && numer !== null ? (
          <button
            type="button"
            className="wskazanie__skok"
            onClick={() => onPokaz(indeks)}
            aria-label={`Pokaż wypowiedź ${numer} w transkrypcie`}
          >
            {`Wypowiedź ${numer}`}
          </button>
        ) : (
          <span className="wskazanie__bez-wypowiedzi">Bez przypisanej wypowiedzi</span>
        )}
        {!wskazanie.cytatZweryfikowany && <Badge>cytat niezweryfikowany</Badge>}
      </div>

      {wskazanie.cytat && <blockquote className="wskazanie__cytat">{wskazanie.cytat}</blockquote>}
      {wskazanie.cytat && !wskazanie.cytatZweryfikowany && (
        <p className="wskazanie__uwaga">
          Nie znaleźliśmy tego cytatu w wypowiedzi słowo w słowo, więc nie zaznaczamy go w transkrypcie.
        </p>
      )}
      {wskazanie.nachodzi && (
        <p className="wskazanie__uwaga">
          Ten cytat nakłada się na inny, więc w transkrypcie jest zaznaczony tylko jeden z nich.
        </p>
      )}

      {wskazanie.uzasadnienie && (
        <p className="wskazanie__opis">
          <Etykieta ton={tonRodzaju(wskazanie.rodzaj)}>
            {mocna ? "Dlaczego to dobre: " : "Jak to obniża wpływ: "}
          </Etykieta>
          {wskazanie.uzasadnienie}
        </p>
      )}
      {mocna && wskazanie.jakRozwijac && (
        <p className="wskazanie__opis">
          <Etykieta>Jak rozwijać: </Etykieta>
          {wskazanie.jakRozwijac}
        </p>
      )}
      {!mocna && <Warianty wskazanie={wskazanie} />}
    </article>
  );
}

export function GrupaWskazan({
  grupa,
  rodzaj,
  onPokaz,
}: {
  grupa: GrupaParametru;
  /** Sekcja, w ktorej stoi grupa: od niej zalezy kolor plakietki z tytulem parametru. */
  rodzaj: FindingKind;
  onPokaz: (indeks: number) => void;
}): React.JSX.Element {
  return (
    <div className="ocena-grupa">
      {/* Tytul parametru jako plakietka oceny (ksztalt BD-1) z kropka w tonie sekcji: tekst zostaje naglowkiem, kolor to
          tylko dodatek (ST-3). */}
      <h4 className="ocena-grupa__tytul">
        <Badge wariant={tonRodzaju(rodzaj)} kropka>
          {grupa.tytul}
        </Badge>
      </h4>
      <div className="ocena-lista">
        {grupa.wskazania.map((w) => (
          <WskazanieOceny wskazanie={w} onPokaz={onPokaz} key={w.id} />
        ))}
      </div>
    </div>
  );
}
